import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Star, ShieldCheck, Scale, MapPin, FileCheck, Leaf, X, ZoomIn, ZoomOut } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, limit, onSnapshot, orderBy, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../contexts/NotificationContext';
import { db } from '../firebase';
import { Order, Product, Review, SelectedVariant, VariantGroup } from '../types';
import { Button } from './Button';

export const ProductDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user, setIsLoginOpen } = useAuth();
  const { showNotification } = useNotification();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedVariants, setSelectedVariants] = useState<SelectedVariant[]>([]);

  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [reviews, setReviews] = useState<Review[]>([]);
  const [myReview, setMyReview] = useState<Review | null>(null);
  const [canReview, setCanReview] = useState(false);
  const [reviewText, setReviewText] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [isSavingReview, setIsSavingReview] = useState(false);

  // Zoom Modal States
  const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);
  const [zoomImageUrl, setZoomImageUrl] = useState('');
  const [zoomLevel, setZoomLevel] = useState(1);

  const openZoomModal = (url: string) => {
    setZoomImageUrl(url);
    setZoomLevel(1);
    setIsZoomModalOpen(true);
  };

  const closeZoomModal = () => {
    setIsZoomModalOpen(false);
    setTimeout(() => {
      setZoomImageUrl('');
      setZoomLevel(1);
    }, 200);
  };

  useEffect(() => {
    const load = async () => {
      if (!id) {
        setProduct(null);
        setIsLoading(false);
        return;
      }
      try {
        setError('');
        setIsLoading(true);
        const ref = doc(db, 'products', id);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setProduct(null);
          return;
        }
        setProduct({ id: snap.id, ...(snap.data() as Omit<Product, 'id'>) });
      } catch (e: any) {
        setError(e?.message || 'Failed to load product');
        setProduct(null);
      } finally {
        setIsLoading(false);
        setSelectedImageIndex(0);
        setSelectedVariants([]);
      }
    };
    load();
  }, [id]);

  useEffect(() => {
    if (!product) {
      setSelectedVariants([]);
      return;
    }
    if (!Array.isArray(product.variants) || product.variants.length === 0) {
      setSelectedVariants([]);
      return;
    }

    setSelectedVariants((prev) => {
      const byId = new Map<string, SelectedVariant>(prev.map((v) => [v.groupId, v] as const));
      const next: SelectedVariant[] = [];

      for (const g of product.variants || []) {
        const existing = byId.get(g.id);
        if (existing) {
          next.push(existing);
          continue;
        }

        if (g.type === 'numeric' && g.numeric) {
          const min = Number(g.numeric.min);
          const max = Number(g.numeric.max);
          const step = Number(g.numeric.step);
          const safeMin = Number.isFinite(min) ? min : 0;
          const safeMax = Number.isFinite(max) ? max : safeMin;
          const safeStep = Number.isFinite(step) && step > 0 ? step : 1;
          next.push({
            groupId: g.id,
            groupName: g.name,
            type: 'numeric',
            value: safeMin,
            unit: g.numeric.unit || '',
            stepPrice: Number(g.numeric.stepPrice) || 0,
            min: safeMin,
            step: safeStep,
          });
          void safeMax;
          continue;
        }

        if (g.type === 'image' && Array.isArray(g.options) && g.options.length > 0) {
          const first = g.options[0];
          next.push({
            groupId: g.id,
            groupName: g.name,
            type: 'image',
            optionId: first.id,
            optionName: first.name,
            optionImageUrl: first.imageUrl,
            optionPrice: Number(first.price) || 0,
          });
          continue;
        }
      }
      return next;
    });
  }, [product]);

  const computedUnitPrice = useMemo(() => {
    if (!product) return 0;
    let price = product.currentPrice;

    for (const v of selectedVariants) {
      if (v.type === 'numeric') {
        const step = Number(v.step);
        const safeStep = Number.isFinite(step) && step > 0 ? step : 1;
        const steps = Math.max(0, Math.round((v.value - v.min) / safeStep));
        price += steps * (Number(v.stepPrice) || 0);
      } else {
        price += Number(v.optionPrice) || 0;
      }
    }
    return price;
  }, [product, selectedVariants]);

  const computedOriginalPrice = useMemo(() => {
    if (!product) return 0;
    const baseCurrent = Number(product.currentPrice) || 0;
    const baseOriginal = Number(product.originalPrice) || 0;
    if (!(baseOriginal > 0) || !(baseOriginal > baseCurrent)) {
      return baseOriginal;
    }

    const discountRatio = 1 - baseCurrent / baseOriginal;
    const denom = 1 - discountRatio;
    if (!Number.isFinite(denom) || denom <= 0) return baseOriginal;
    const nextOriginal = computedUnitPrice / denom;
    return Number.isFinite(nextOriginal) ? Math.round(nextOriginal) : baseOriginal;
  }, [product, computedUnitPrice]);

  useEffect(() => {
    if (!id) {
      setReviews([]);
      return;
    }

    const q = query(collection(db, 'reviews'), where('productId', '==', id), limit(50));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Review, 'id'>) } as Review));
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setReviews(list);
    });
    return () => unsub();
  }, [id]);

  useEffect(() => {
    if (!id || !user?.mobile) {
      setCanReview(false);
      setMyReview(null);
      return;
    }

    const unsubMine = onSnapshot(doc(db, 'reviews', `${id}_${user.mobile}`), (snap) => {
      if (snap.exists()) {
        const r = { id: snap.id, ...(snap.data() as Omit<Review, 'id'>) } as Review;
        setMyReview(r);
        setReviewText(typeof r.text === 'string' ? r.text : '');
        setReviewRating(Number(r.rating) || 5);
      } else {
        setMyReview(null);
      }
    });

    const qOrders = query(collection(db, 'orders'), where('userId', '==', user.mobile), where('status', '==', 'Delivered'));
    const unsubOrders = onSnapshot(qOrders, (snap) => {
      const delivered = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Order, 'id'>) } as Order));
      const ok = delivered.some((o) => Array.isArray(o.items) && o.items.some((it: any) => it?.id === id));
      setCanReview(ok);
    });

    return () => {
      unsubMine();
      unsubOrders();
    };
  }, [id, user?.mobile]);

  if (isLoading) return <div className="p-20 text-center text-stone-600 text-lg">Loading details...</div>;

  if (error) return <div className="p-20 text-center text-red-600 text-lg">{error}</div>;

  if (!product) return <div className="p-20 text-center font-serif text-2xl text-stone-800">Product not found</div>;

  const variants: VariantGroup[] = Array.isArray(product.variants) ? product.variants : [];

  const handleAddToCart = () => {
    addToCart(product, { selectedVariants, unitPrice: computedUnitPrice });
    showNotification(`${product.name} added to cart!`, 'success');
  };

  const discountPercentage = Math.round(((product.originalPrice - product.currentPrice) / product.originalPrice) * 100);

  const saveReview = async () => {
    if (!id || !product) return;
    if (!user?.mobile) {
      setIsLoginOpen(true);
      return;
    }
    if (!canReview) {
      showNotification('You can review only after delivery.', 'error');
      return;
    }

    const text = reviewText.trim().slice(0, 100);
    const rating = Math.min(5, Math.max(1, Math.round(Number(reviewRating) || 5)));
    if (!text) {
      showNotification('Review text required.', 'error');
      return;
    }

    try {
      setIsSavingReview(true);
      const displayName = (user as any).name || (user as any).fullName || user.mobile;
      await setDoc(doc(db, 'reviews', `${id}_${user.mobile}`), {
        productId: id,
        userId: user.mobile,
        userName: displayName,
        rating,
        text,
        createdAt: myReview?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      showNotification('Review saved', 'success');
    } catch (e: any) {
      showNotification(e?.message || 'Failed to save review', 'error');
    } finally {
      setIsSavingReview(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 animate-[fadeIn_0.3s_ease-out]">
      <button 
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-stone-900 mb-8 transition-colors"
      >
        <ArrowLeft size={18} /> Back to Collection
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
        {/* Left Column: Images */}
        <div className="space-y-4">
          <div 
            onClick={() => openZoomModal(product.images[selectedImageIndex])}
            className="relative aspect-square bg-stone-50 rounded-2xl overflow-hidden border border-stone-100 shadow-sm group cursor-zoom-in"
          >
            <img 
              src={product.images[selectedImageIndex]} 
              alt={product.name} 
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            {/* Visual cue to zoom */}
            <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur p-2 rounded-full shadow-md text-stone-700 opacity-0 group-hover:opacity-100 transition-opacity">
              <ZoomIn size={20} />
            </div>
          </div>
          <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
            {product.images.map((img, idx) => (
              <button 
                key={idx}
                onClick={() => setSelectedImageIndex(idx)}
                className={`w-24 h-24 flex-shrink-0 rounded-xl overflow-hidden border-2 transition-all ${
                  selectedImageIndex === idx 
                    ? 'border-orange-500 opacity-100' 
                    : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Right Column: Details */}
        <div className="flex flex-col">
          <div className="mb-4 flex items-center gap-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-orange-700">
              {product.category}
            </span>
            {product.natural && (
              <span className="px-3 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full flex items-center gap-1 border border-green-200">
                <Leaf size={12} /> 100% Natural
              </span>
            )}
          </div>
          
          <h1 className="text-3xl md:text-4xl font-serif text-stone-900 mb-4 leading-tight">
            {product.name}
          </h1>

          <div className="flex items-center gap-4 mb-6">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star 
                  key={i} 
                  size={16} 
                  className={i < Math.floor(product.rating) ? "fill-orange-500 text-orange-500" : "text-stone-200"} 
                />
              ))}
              <span className="text-sm font-medium text-stone-500 ml-2">({product.rating} Rating)</span>
            </div>
            {product.quantityLeft !== undefined && product.quantityLeft < 10 && product.quantityLeft > 0 && (
              <>
                <span className="w-1.5 h-1.5 rounded-full bg-stone-300"></span>
                <span className="text-sm font-medium text-red-600 bg-red-50 px-3 py-1 rounded-full">
                  Only {product.quantityLeft} Left
                </span>
              </>
            )}
            <span className="w-1.5 h-1.5 rounded-full bg-stone-300"></span>
            <span className="text-sm font-medium text-stone-400">SKU: {product.id.substring(0,8)}</span>
          </div>

          <div className="mb-8">
             <div className="flex items-end gap-4 mb-2">
               <span className="text-4xl font-semibold text-stone-900">₹{computedUnitPrice.toLocaleString('en-IN')}</span>
               {computedOriginalPrice > computedUnitPrice && (
                 <>
                   <span className="text-xl text-stone-400 line-through mb-1">
                     ₹{computedOriginalPrice.toLocaleString('en-IN')}
                   </span>
                   <span className="text-sm font-bold text-red-600 mb-1.5 bg-red-50 px-2 py-1 rounded-md">
                     {discountPercentage}% OFF
                   </span>
                 </>
               )}
             </div>
             <p className="text-sm text-stone-500">Price includes delivery and insurance.</p>
          </div>

          <div className="space-y-8 mb-10">
            {product.details && (
              <div className="p-5 bg-stone-50 rounded-2xl border border-stone-100">
                <h3 className="text-sm font-semibold text-stone-900 mb-2">Quick Information</h3>
                <p className="text-base text-stone-600 leading-relaxed whitespace-pre-wrap">
                  {product.details}
                </p>
              </div>
            )}

            {product.description && (
              <div className="space-y-3">
                <h3 className="text-lg font-serif text-stone-900">About this Stone</h3>
                <p className="text-base text-stone-600 leading-relaxed whitespace-pre-wrap">
                  {product.description}
                </p>
              </div>
            )}
          </div>

          {/* Variants Selection */}
          {variants.length > 0 && (
            <div className="mb-10 space-y-6">
              {variants.map((g) => {
                const selected = selectedVariants.find((v) => v.groupId === g.id);

                if (g.type === 'numeric' && g.numeric) {
                  const currentValue = selected && selected.type === 'numeric' ? selected.value : g.numeric.min;
                  const min = Number(g.numeric.min);
                  const max = Number(g.numeric.max);
                  const step = Number(g.numeric.step);

                  return (
                    <div key={g.id} className="p-5 rounded-2xl border border-stone-200 bg-white">
                      <div className="flex items-center justify-between gap-4 mb-4">
                        <div>
                          <p className="text-sm font-semibold text-stone-900">{g.name}</p>
                          <p className="text-base text-stone-600 mt-1">{currentValue}{g.numeric.unit}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Increment</p>
                          <p className="text-sm font-medium text-stone-700">{g.numeric.step} {g.numeric.unit}</p>
                        </div>
                      </div>

                      <input
                        type="range"
                        min={min}
                        max={max}
                        step={Number.isFinite(step) && step > 0 ? step : 1}
                        value={currentValue}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setSelectedVariants((prev) => {
                            const without = prev.filter((v) => v.groupId !== g.id);
                            return [
                              ...without,
                              {
                                groupId: g.id,
                                groupName: g.name,
                                type: 'numeric',
                                value,
                                unit: g.numeric?.unit || '',
                                stepPrice: Number(g.numeric?.stepPrice) || 0,
                                min: Number(g.numeric?.min) || 0,
                                step: Number(g.numeric?.step) || 1,
                              },
                            ];
                          });
                        }}
                        className="w-full accent-orange-600 h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer"
                      />

                      <div className="flex items-center justify-between mt-3 text-sm font-medium text-stone-500">
                        <span>{min}{g.numeric.unit}</span>
                        <span>{max}{g.numeric.unit}</span>
                      </div>
                    </div>
                  );
                }

                if (g.type === 'image' && Array.isArray(g.options) && g.options.length > 0) {
                  const selectedOptionId = selected && selected.type === 'image' ? selected.optionId : g.options[0].id;

                  return (
                    <div key={g.id} className="space-y-3">
                      <p className="text-sm font-semibold text-stone-900">{g.name}</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        {g.options.map((opt) => {
                          const isActive = opt.id === selectedOptionId;
                          return (
                            <button
                              key={opt.id}
                              type="button"
                              onClick={() => {
                                setSelectedVariants((prev) => {
                                  const without = prev.filter((v) => v.groupId !== g.id);
                                  return [
                                    ...without,
                                    {
                                      groupId: g.id,
                                      groupName: g.name,
                                      type: 'image',
                                      optionId: opt.id,
                                      optionName: opt.name,
                                      optionImageUrl: opt.imageUrl,
                                      optionPrice: Number(opt.price) || 0,
                                    },
                                  ];
                                });
                              }}
                              className={`text-left rounded-xl border overflow-hidden transition-all ${
                                isActive 
                                  ? 'border-orange-500 ring-1 ring-orange-500 bg-orange-50/30' 
                                  : 'border-stone-200 bg-white hover:border-stone-300'
                              }`}
                            >
                              <div className="relative aspect-[4/3] bg-stone-50 border-b border-stone-100 group/img">
                                <img src={opt.imageUrl} alt={opt.name} className="w-full h-full object-cover" />
                                {/* Zoom overlay button to prevent selecting when they just want to inspect */}
                                <div 
                                  onClick={(e) => { 
                                    e.stopPropagation(); // Stops the button from selecting the variant
                                    openZoomModal(opt.imageUrl); 
                                  }}
                                  className="absolute bottom-2 right-2 p-1.5 bg-white/90 shadow-sm backdrop-blur rounded-lg text-stone-600 hover:text-orange-600 hover:bg-white transition-colors cursor-zoom-in"
                                  title="Inspect Image"
                                >
                                  <ZoomIn size={14} />
                                </div>
                              </div>
                              <div className="p-3">
                                <p className="text-sm font-medium text-stone-900 truncate">{opt.name}</p>
                                <p className="text-sm text-stone-500 mt-0.5">+ ₹{(Number(opt.price) || 0).toLocaleString('en-IN')}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                return null;
              })}
            </div>
          )}

          <div className="flex gap-4 mb-12">
            <Button onClick={handleAddToCart} size="lg" className="flex-1 py-4 text-base font-semibold shadow-lg rounded-xl">
              Add to Cart
            </Button>
          </div>

          {/* Product Specifications Grid */}
          <div className="grid grid-cols-2 gap-4 mb-12">
            <div className="p-4 bg-stone-50 rounded-2xl border border-stone-100">
              <div className="flex items-center gap-2 text-stone-500 mb-2">
                <Scale size={16} /> <span className="text-xs font-semibold uppercase tracking-wider">Weight</span>
              </div>
              <p className="font-medium text-base text-stone-900">{product.weight}</p>
            </div>
            <div className="p-4 bg-stone-50 rounded-2xl border border-stone-100">
              <div className="flex items-center gap-2 text-stone-500 mb-2">
                <MapPin size={16} /> <span className="text-xs font-semibold uppercase tracking-wider">Origin</span>
              </div>
              <p className="font-medium text-base text-stone-900">{product.origin}</p>
            </div>
            <div className="p-4 bg-stone-50 rounded-2xl border border-stone-100">
              <div className="flex items-center gap-2 text-stone-500 mb-2">
                <FileCheck size={16} /> <span className="text-xs font-semibold uppercase tracking-wider">Certificate</span>
              </div>
              <p className="font-medium text-base text-stone-900">{product.certificate}</p>
            </div>
            <div className="p-4 bg-stone-50 rounded-2xl border border-stone-100">
              <div className="flex items-center gap-2 text-stone-500 mb-2">
                <ShieldCheck size={16} /> <span className="text-xs font-semibold uppercase tracking-wider">Guarantee</span>
              </div>
              <p className="font-medium text-base text-stone-900">{product.guarantee}</p>
            </div>
          </div>

          {product.about && (
            <div className="border-t border-stone-200 pt-8">
              <h3 className="text-lg font-serif text-stone-900 mb-4">Registry Dossier</h3>
              <p className="text-stone-600 italic leading-relaxed text-base">
                "{product.about}"
              </p>
            </div>
          )}

          {/* Reviews Section */}
          <div className="border-t border-stone-200 pt-12 mt-12">
            <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-10">
              <div>
                <h3 className="text-2xl font-serif text-stone-900 mb-2">Customer Reviews</h3>
                <p className="text-sm text-stone-500">Authentic community feedback</p>
              </div>
              
              {user?.mobile && canReview && (
                <div className="w-full md:max-w-md bg-stone-50 p-6 rounded-2xl border border-stone-200">
                  <p className="text-sm font-semibold text-stone-900 mb-4">{myReview ? 'Update Your Review' : 'Write a Review'}</p>
                  <div className="flex gap-2 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button key={star} onClick={() => setReviewRating(star)} className="focus:outline-none hover:scale-110 transition-transform">
                        <Star size={24} className={star <= reviewRating ? "fill-orange-500 text-orange-500" : "text-stone-300"} />
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={reviewText}
                    onChange={(e) => setReviewText(e.target.value.slice(0, 100))}
                    placeholder="Share your experience (Max 100 characters)..."
                    className="w-full px-4 py-3 border border-stone-300 rounded-xl text-sm outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 min-h-[100px] mb-3 transition-shadow"
                  />
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-stone-500">{reviewText.length}/100</span>
                    <Button onClick={saveReview} disabled={isSavingReview} className="rounded-lg px-6 py-2 text-sm font-semibold">
                      {isSavingReview ? 'Saving...' : (myReview ? 'Update' : 'Publish')}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-6">
              {reviews.length === 0 ? (
                <div className="py-12 text-center bg-stone-50 rounded-2xl border border-dashed border-stone-300">
                  <p className="text-sm text-stone-500">No reviews yet. Be the first to share your thoughts after purchase!</p>
                </div>
              ) : (
                reviews.map((r) => (
                  <div key={r.id} className="p-6 bg-white border border-stone-200 rounded-2xl shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center text-stone-600 text-sm font-semibold">
                          {r.userName?.charAt(0) || 'C'}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-stone-900 mb-1">{r.userName || 'Customer'}</p>
                          <div className="flex gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} size={12} className={i < r.rating ? "fill-orange-500 text-orange-500" : "text-stone-200"} />
                            ))}
                          </div>
                        </div>
                      </div>
                      <p className="text-sm text-stone-400">{r.createdAt?.seconds ? new Date(r.createdAt.seconds * 1000).toLocaleDateString() : 'Recent'}</p>
                    </div>
                    <p className="text-base text-stone-700 leading-relaxed">"{r.text}"</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* --- Image Zoom Modal --- */}
      {isZoomModalOpen && (
        <div className="fixed inset-0 z-[100000] bg-black/95 flex flex-col items-center justify-center touch-none animate-[fadeIn_0.2s_ease-out]">
          
          {/* Header Controls */}
          <div className="absolute top-0 left-0 w-full p-4 flex justify-between items-center z-20 bg-gradient-to-b from-black/50 to-transparent pointer-events-none">
            <div className="pointer-events-auto text-white/70 text-sm font-medium px-2">
               Image Preview
            </div>
            <button 
              onClick={closeZoomModal} 
              className="pointer-events-auto text-white p-2 bg-white/10 hover:bg-white/20 rounded-full backdrop-blur transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Image Container with Overflow */}
          <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
            <img 
              src={zoomImageUrl} 
              alt="Zoomed Product" 
              style={{ transform: `scale(${zoomLevel})` }}
              className="max-w-full max-h-full object-contain transition-transform duration-200 origin-center select-none"
            />
          </div>

          {/* Zoom Controls Overlay */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-white/10 p-2 rounded-full backdrop-blur-md z-20">
             <button 
               onClick={() => setZoomLevel(prev => Math.max(prev - 0.5, 1))} 
               disabled={zoomLevel === 1} 
               className="text-white p-3 hover:bg-white/20 rounded-full disabled:opacity-30 transition-colors"
             >
               <ZoomOut size={20} />
             </button>
             <span className="text-white text-sm font-bold w-12 text-center">
               {Math.round(zoomLevel * 100)}%
             </span>
             <button 
               onClick={() => setZoomLevel(prev => Math.min(prev + 0.5, 4))} 
               disabled={zoomLevel === 4} 
               className="text-white p-3 hover:bg-white/20 rounded-full disabled:opacity-30 transition-colors"
             >
               <ZoomIn size={20} />
             </button>
          </div>
        </div>
      )}
    </div>
  );
};
